import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import leftArrow from '../../assets/icons/left-arrow.svg';
import avatar from '../../assets/images/avatar-svg.svg';
// import './AuthHeader.scss';
import { CompanyDto, UserDto } from '@jaam/commons/dtos';
import { cn } from '../../helpers';
import { MOBILE_BREAK_POINT } from '../../constants';
import ChevronLeft from '../icon/ChevronLeft';
import LanguageSelector from '../languageSelector/LanguageSelector';
import {
  Layer24Regular,
  Apps24Regular,
  Headset24Regular,
  ChevronDown16Regular,
  WalletCreditCard24Regular,
  Document24Regular,
  PeopleTeam24Regular,
} from '@fluentui/react-icons';

interface IProps {
  company: CompanyDto;
  user: UserDto;
  classes?: string;
  menu?: React.ReactNode;
  showBackButton: boolean;
  setCloseSettingsPanel: (value: boolean) => void;
  backRoute: string | null;
  closeSettingsPanel: boolean;
  appIconUrl?: string;
}

const getApplicationsDropdownItems = () => [
  {
    href: process.env['NX_ACCOUNTS_PUBLIC_URL'] || '/accounts',
    labelKey: 'header.applicationsAccounts',
    labelFallback: 'Accounts',
    Icon: WalletCreditCard24Regular,
  },
  {
    href: process.env['NX_EDECLARATION_PUBLIC_URL'] || '/edeclaration',
    labelKey: 'header.applicationsEdeclaration',
    labelFallback: 'eDeclaration',
    Icon: Document24Regular,
  },
  {
    href: process.env['NX_PAYROLLR_PUBLIC_URL'] || '/workforce',
    labelKey: 'header.applicationsWorkforce',
    labelFallback: 'Workforce',
    Icon: PeopleTeam24Regular,
  },
];

const APPLICATIONS_DROPDOWN_ITEMS = getApplicationsDropdownItems();

/**
 * Détermine si l'application correspondant à cet href est celle affichée.
 * Pour une URL absolue (ex. NX_PAYROLLR_PUBLIC_URL = http://localhost:4201), on vérifie
 * que l'URL actuelle (origin + pathname) commence par cette base.
 * Pour un chemin relatif (ex. /workforce), on compare au pathname actuel.
 */
const isCurrentApplication = (href: string): boolean => {
  if (typeof window === 'undefined') return false;
  const currentUrl = window.location.origin + window.location.pathname;
  const currentPath = window.location.pathname;

  if (href.startsWith('http')) {
    const base = href.replace(/\/$/, '');
    return currentUrl === base || currentUrl.startsWith(base + '/');
  }
  return currentPath === href || currentPath.startsWith(`${href}/`);
};

const ApplicationsDropdown = memo(function ApplicationsDropdown() {
  const { t } = useTranslation();
  const [applicationsDropdownOpen, setApplicationsDropdownOpen] = useState(false);
  const applicationsDropdownCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const activeHrefs = useMemo(
    () =>
      new Set(
        APPLICATIONS_DROPDOWN_ITEMS.filter(({ href }) => isCurrentApplication(href)).map(
          ({ href }) => href,
        ),
      ),
    [],
  );

  useEffect(() => {
    return () => {
      if (applicationsDropdownCloseTimeout.current) {
        clearTimeout(applicationsDropdownCloseTimeout.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (applicationsDropdownCloseTimeout.current) {
      clearTimeout(applicationsDropdownCloseTimeout.current);
      applicationsDropdownCloseTimeout.current = null;
    }
    setApplicationsDropdownOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    applicationsDropdownCloseTimeout.current = setTimeout(() => {
      setApplicationsDropdownOpen(false);
      applicationsDropdownCloseTimeout.current = null;
    }, 150);
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        className="flex items-center gap-1.5 text-[#010202] hover:opacity-70 transition-opacity cursor-default"
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={applicationsDropdownOpen}
      >
        <Apps24Regular className="w-5 h-5" />
        <span className="text-base max-xl:hidden">{t('header.applications')}</span>
        <ChevronDown16Regular className="!hidden w-4 h-4 md:!block" />
      </span>
      {applicationsDropdownOpen && (
        <div
          className="absolute md:left-0 top-full mt-0 pt-1 min-w-[180px] z-30 -left-[50px] max-md:w-fit "
          role="menu"
        >
          <div className="bg-white rounded-lg w-fit shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-neutral-90 py-1">
            {APPLICATIONS_DROPDOWN_ITEMS.map(({ href, labelKey, labelFallback, Icon }) => {
              const isActive = activeHrefs.has(href);
              return (
                <a
                  key={href}
                  href={href}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-neutral-12 hover:bg-blue-95 transition-colors first:rounded-t-[6px] last:rounded-b-[6px]',
                    isActive && 'bg-blue-95',
                  )}
                  role="menuitem"
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="text-base">
                    {t(labelKey) !== labelKey ? t(labelKey) : labelFallback}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

const AuthHeaderComponent = (props: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const [headerShow, setHeaderShow] = useState<boolean>(false);

  const navigate = useNavigate();
  const {
    company,
    user,
    classes = '',
    showBackButton,
    setCloseSettingsPanel,
    backRoute,
    appIconUrl,
    closeSettingsPanel,
  } = props;

  const handleOpenSettings = useCallback(() => {
    setCloseSettingsPanel(true);
  }, [setCloseSettingsPanel]);

  const handleCloseSettings = useCallback(() => {
    setCloseSettingsPanel(false);
  }, [setCloseSettingsPanel]);

  /** Paths without a leading `/` are resolved relative to the current URL in RR6 (wrong from nested routes). */
  const authBackRoute = useMemo(() => {
    if (!backRoute) return -1;
    if (backRoute === '/') return '/home';
    if (
      typeof backRoute === 'string' &&
      !backRoute.startsWith('/') &&
      !backRoute.startsWith('http')
    ) {
      return `/${backRoute}`;
    }
    return backRoute;
  }, [backRoute]);

  const handleBack = useCallback(() => {
    typeof authBackRoute === 'number'
      ? navigate(authBackRoute)
      : navigate(`${authBackRoute}`);
  }, [authBackRoute, navigate]);

  useEffect(() => {
    const handleResize = () => setHeaderShow(window.innerWidth < MOBILE_BREAK_POINT);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return (
    <div className="relative left-0 right-0 z-20 h-10 shadow-headerBoxShadow ">
      {!(closeSettingsPanel && headerShow) && (
        <header
          className={` bg-white h-full ${closeSettingsPanel && 'hidden sm:block'
            } ${classes}`}
        >
          <div className="px-3  grid grid-cols-12 max-w-[1440px] md:px-12 mx-auto  relative h-full ">
            <div className="flex items-center justify-start col-span-4 gap-4 ">
              {!showBackButton && (
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-2 py-1 rounded-full outline-none hover:bg-neutral-98 "
                  data-testid="company"
                  onClick={handleOpenSettings}
                >
                  <img
                    className="h-[28px] w-[28px] border-2 rounded-full border-primary"
                    src={user.picture ? user.picture : avatar}
                    alt="avatar"
                  />

                  <span className="hidden md:inline-block text-[.85rem] text-[#010202] ">
                    <span className="text-uppercase" data-testid="companyName">
                      {company.name}
                    </span>
                  </span>
                </button>
              )}
              {showBackButton && (
                <button
                  onClick={handleBack}
                  type="button"
                  className="absolute top-0 flex flex-row items-center p-1 mt-1 bg-transparent outline-none left-8 "
                >
                  <span className=" text-blue-00">
                    <ChevronLeft />
                  </span>
                  <span className="text-blue-00 text-base ml-[0.5rem] ">
                    {t('buttons.back')}
                  </span>
                </button>
              )}
            </div>
            <div className="flex items-center justify-center col-span-4 ">
              <Link to="/home">
                <img className="icon" src={appIconUrl} alt="" />
              </Link>
            </div>
            <div className="flex items-center justify-end col-span-4 gap-4">
              <div className="items-center gap-3 flex lg:gap-4 ">
                <a
                  href="/companies"
                  className="md:flex hidden items-center gap-1.5 text-[#010202] hover:opacity-70 transition-opacity"
                >
                  <Layer24Regular className="w-5 h-5" />
                  <span className="text-base max-xl:hidden">{t('header.myCompanies')}</span>
                </a>
                <ApplicationsDropdown />
                <a
                  href="/help"
                  className="hidden md:flex items-center gap-1.5 text-[#010202] hover:opacity-70 transition-opacity"
                >
                  <Headset24Regular className="w-5 h-5" />
                  <span className="text-base max-xl:hidden">{t('header.help')}</span>
                </a>
              </div>
              <LanguageSelector />
            </div>

            <div
              className={`${closeSettingsPanel ? 'block' : 'hidden'
                } absolute -top-[40px] left-0 `}
            // onClick={(nextShow: boolean) => setCloseSettingsPanel(nextShow)}
            >
              {props.menu}
            </div>
          </div>
        </header>
      )}
      {closeSettingsPanel && headerShow && (
        <header
          className={`${classes} flex  flex-row sm:hidden h-full relative bg-[#f3f4f5]`}
        >
          <button
            onClick={handleCloseSettings}
            type="button"
            className="absolute top-0 flex flex-row p-1 mt-1 bg-transparent outline-none left-8 "
          >
            <img className=" mr-[0.5rem] " src={leftArrow} alt="back" />
            <div className="flex">
              <div className="mr-auto">
                <span className="text-blue-00  text-[16px]  ">
                  {' '}
                  {t('buttons.back')}
                </span>
              </div>
            </div>
          </button>
          <div className="w-full text-center flex items-center justify-center text-[1.25rem] leading-[24px] font-bold  ">
            {t('menu.profile')}
          </div>
          <div
            className={`${closeSettingsPanel ? 'block' : 'hidden'
              } absolute top-0 left-0 right-0 `}
          // onClick={(nextShow: boolean) => setCloseSettingsPanel(nextShow)}
          >
            {props.menu}
          </div>
        </header>
      )}
    </div>
  );
};

export const AuthHeader = memo(AuthHeaderComponent);

export default AuthHeader;
